import React, { useState, useMemo } from "react";
import { Search, X, Check, Users } from "lucide-react";

export function Avatar({ name }) {
  const initials = name.split(" ").map((p) => p[0]).slice(0, 2).join("");
  return (
    <div style={{
      width: 34, height: 34, borderRadius: 999, background: "#0F2A43", color: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 12, fontWeight: 700, flexShrink: 0, letterSpacing: 0.3,
    }}>
      {initials}
    </div>
  );
}

export function RecipientModal({ title, people, selectedIds, onToggle, onSelectAll, onClear, onClose, onDone, fields }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return people;
    return people.filter((p) =>
      p.name.toLowerCase().includes(q) || (p.phone && p.phone.includes(q))
    );
  }, [query, people]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id));

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15, 23, 33, 0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 14, width: "100%", maxWidth: 480,
          maxHeight: "82vh", display: "flex", flexDirection: "column",
          boxShadow: "0 20px 60px rgba(15,23,33,0.25)", overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: "1px solid #EEF0F2" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F2A43" }}>{title}</div>
              <div style={{ fontSize: 12.5, color: "#6B7686", marginTop: 2 }}>
                {selectedIds.size} of {people.length} selected
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                border: "none", background: "#F3F4F6", borderRadius: 8, width: 30, height: 30,
                display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
              }}
              aria-label="Close"
            >
              <X size={16} color="#6B7686" />
            </button>
          </div>

          <div style={{ position: "relative", marginTop: 12 }}>
            <Search size={15} color="#9AA3AF" style={{ position: "absolute", left: 10, top: 10 }} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or phone…"
              style={{
                width: "100%", padding: "8px 10px 8px 32px", borderRadius: 8,
                border: "1px solid #E2E5E9", fontSize: 13.5, outline: "none", boxSizing: "border-box",
                color: "#1F2937",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: 14, marginTop: 10 }}>
            <button
              onClick={() => onSelectAll(filtered.map((p) => p.id))}
              style={{ border: "none", background: "none", color: "#0F5FBF", fontSize: 12.5, fontWeight: 600, cursor: "pointer", padding: 0 }}
            >
              {allFilteredSelected ? "Unselect all" : "Select all shown"}
            </button>
            <button
              onClick={onClear}
              style={{ border: "none", background: "none", color: "#6B7686", fontSize: 12.5, fontWeight: 600, cursor: "pointer", padding: 0 }}
            >
              Clear selection
            </button>
          </div>
        </div>

        {/* List */}
        <div style={{ overflowY: "auto", padding: "6px 8px", flex: 1 }}>
          {filtered.length === 0 && (
            <div style={{ padding: "32px 12px", textAlign: "center", color: "#9AA3AF", fontSize: 13 }}>
              No one matches "{query}".
            </div>
          )}
          {filtered.map((p) => {
            const checked = selectedIds.has(p.id);
            return (
              <div
                key={p.id}
                onClick={() => onToggle(p.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "9px 10px",
                  borderRadius: 10, cursor: "pointer",
                  background: checked ? "#F0F6FF" : "transparent",
                }}
                onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = "#F8F9FA"; }}
                onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = checked ? "#F0F6FF" : "transparent"; }}
              >
                <div
                  style={{
                    width: 18, height: 18, borderRadius: 5, border: checked ? "none" : "1.5px solid #C9CDD3",
                    background: checked ? "#0F5FBF" : "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}
                >
                  {checked && <Check size={12} color="#fff" strokeWidth={3} />}
                </div>
                <Avatar name={p.name} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "#1F2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {p.name}
                  </div>
                  <div style={{ fontSize: 12, color: "#8B94A3" }}>
                    {p.phone}{fields === "officers" && p.role ? ` · ${p.role}` : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 20px", borderTop: "1px solid #EEF0F2", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              padding: "9px 16px", borderRadius: 9, border: "1px solid #E2E5E9", background: "#fff",
              fontSize: 13.5, fontWeight: 600, color: "#4B5563", cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onDone}
            style={{
              padding: "9px 18px", borderRadius: 9, border: "none", background: "#1A4985",
              fontSize: 13.5, fontWeight: 600, color: "#fff", cursor: "pointer",
            }}
          >
            Confirm {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

export function RecipientGroupCard({ icon, title, subtitle, mode, setMode, count, total, active, setActive, onOpenPicker }) {
  return (
    <div
      style={{
        flex: 1, border: `1.5px solid ${active ? "#1A4985" : "#E5E7EB"}`,
        borderRadius: 14, padding: 16, background: active ? "#FBFCFE" : "#fff",
        transition: "border-color 120ms ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            style={{ width: 17, height: 17, accentColor: "#1A4985", cursor: "pointer" }}
          />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0F2A43", display: "flex", alignItems: "center", gap: 6 }}>
              {icon} {title}
            </div>
            <div style={{ fontSize: 12, color: "#8B94A3", marginTop: 1 }}>{subtitle}</div>
          </div>
        </label>
      </div>

      <div
        style={{
          marginTop: 14, display: "flex", gap: 8, opacity: active ? 1 : 0.45,
          pointerEvents: active ? "auto" : "none",
        }}
      >
        <button
          onClick={() => setMode("all")}
          style={{
            flex: 1, padding: "8px 10px", borderRadius: 9, cursor: "pointer",
            border: mode === "all" ? "1.5px solid #1A4985" : "1.5px solid #E5E7EB",
            background: mode === "all" ? "#1A4985" : "#fff",
            color: mode === "all" ? "#fff" : "#4B5563",
            fontSize: 12.5, fontWeight: 600,
          }}
        >
          All ({total})
        </button>
        <button
          onClick={() => { setMode("select"); onOpenPicker(); }}
          style={{
            flex: 1, padding: "8px 10px", borderRadius: 9, cursor: "pointer",
            border: mode === "select" ? "1.5px solid #1A4985" : "1.5px solid #E5E7EB",
            background: mode === "select" ? "#1A4985" : "#fff",
            color: mode === "select" ? "#fff" : "#4B5563",
            fontSize: 12.5, fontWeight: 600,
          }}
        >
          Select…
        </button>
      </div>

      {active && mode === "select" && (
        <button
          onClick={onOpenPicker}
          style={{
            marginTop: 10, width: "100%", padding: "8px 10px", borderRadius: 9,
            border: "1px dashed #C9CDD3", background: "#fff", cursor: "pointer",
            fontSize: 12.5, color: "#0F5FBF", fontWeight: 600,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}
        >
          <Users size={13} />
          {count > 0 ? `${count} chosen — edit selection` : "Choose recipients"}
        </button>
      )}
    </div>
  );
}
